import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { form, FormField, FormRoot, maxLength, minLength, required } from '@angular/forms/signals';
import { Router, RouterLink } from '@angular/router';
import { AuthError } from '@azure/msal-browser';
import { AuthService } from '@core/auth/auth.service';
import { EntraIdService } from '@core/auth/entra-id/entra-id.service';
import { LoginRequest } from '@core/auth/models/login.model';
import { isCallerHandledError } from '@core/interceptors/http-error.interceptor';
import { AppError } from '@core/models/app-error.model';
import { LucideArrowRight, LucideLock, LucideMail, LucideUser } from '@lucide/angular';
import { Alert } from '@ui/primitives/alert/alert';
import { Button } from '@ui/primitives/button/button';
import { Checkbox } from '@ui/primitives/checkbox/checkbox';
import { Input } from '@ui/primitives/input/input';
import { firstValueFrom } from 'rxjs';

const MICROSOFT_CANCELLED_MESSAGE = 'Se canceló el inicio de sesión con Microsoft.';
const MICROSOFT_GENERIC_ERROR_MESSAGE = 'No se pudo iniciar sesión con Microsoft. Intenta nuevamente.';
const MICROSOFT_PERMISSION_ERROR_MESSAGE = 'Tu cuenta de Microsoft no tiene permisos para acceder a Incentivos.';

function isAppError(error: unknown): error is AppError {
  return typeof error === 'object' && error !== null && 'statusCode' in error && 'messages' in error;
}

const MICROSOFT_ERROR_MESSAGES: Record<string, string> = {
  access_denied: MICROSOFT_PERMISSION_ERROR_MESSAGE,
  consent_required: MICROSOFT_PERMISSION_ERROR_MESSAGE,
  unauthorized_client: MICROSOFT_PERMISSION_ERROR_MESSAGE,
};

@Component({
  selector: 'app-login-form',
  imports: [
    FormField,
    FormRoot,
    Input,
    Checkbox,
    Button,
    RouterLink,
    Alert,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './login-form.html',
  host: {
    '(window:pageshow)': 'onPageShow($event)',
  },
})
export class LoginForm {
  private readonly _router = inject(Router);
  private readonly _authService = inject(AuthService);
  private readonly _entraIdService = inject(EntraIdService);
  protected readonly userIcon = LucideUser;
  protected readonly lockIcon = LucideLock;
  protected readonly arrowRightIcon = LucideArrowRight;
  protected readonly mailIcon = LucideMail;

  protected readonly loginModel = signal<LoginRequest>({
    usuario: '',
    password: '',
    idPais: 'MX',
    remember: false,
  });

  protected readonly formError = signal<AppError | null>(null);
  protected readonly isLoggingIn = signal(false);
  protected readonly isLoggingInWithMicrosoft = signal(false);
  protected readonly isBusy = computed(() => this.isLoggingIn() || this.isLoggingInWithMicrosoft());

  protected readonly loginForm = form(
    this.loginModel,
    (path) => {
      required(path.usuario, { message: 'El usuario es requerido' });
      maxLength(path.usuario, 50);

      required(path.password, { message: 'La contraseña es requerida' });
      minLength(path.password, 6);
    },
    {
      submission: {
        action: async () => {
          this.formError.set(null);
          this.isLoggingIn.set(true);

          try {
            await firstValueFrom(
              this._authService.login(this.loginModel())
            );
            await this._router.navigate(['home']);

          } catch (error) {
            if (isCallerHandledError(error as AppError)) {
              this.formError.set(error as AppError);
            }
          } finally {
            this.isLoggingIn.set(false);
          }
        },
      },
    },
  );

  constructor() {
    this.resumeMicrosoftSignIn();
  }

  protected async loginWithMicrosoft(): Promise<void> {
    this.formError.set(null);
    this.isLoggingInWithMicrosoft.set(true);

    try {
      await this._entraIdService.signIn();
    } catch (error) {
      this._entraIdService.clearPendingSignIn();
      this.showMicrosoftError(error);
      this.isLoggingInWithMicrosoft.set(false);
    }
  }

  protected onPageShow(event: PageTransitionEvent): void {
    if (!event.persisted || !this._entraIdService.hasPendingSignIn()) return;

    this._entraIdService.clearPendingSignIn();
    this.isLoggingInWithMicrosoft.set(false);
    this.setFormError(MICROSOFT_CANCELLED_MESSAGE);
  }

  private async resumeMicrosoftSignIn(): Promise<void> {
    if (!this._entraIdService.hasPendingSignIn()) return;

    this.isLoggingInWithMicrosoft.set(true);

    try {
      const result = await this._entraIdService.completeSignIn();
      if (!result) {
        this.setFormError(MICROSOFT_CANCELLED_MESSAGE);
        return;
      }

      try {
        await firstValueFrom(
          this._authService.loginWithEntraId({ idPais: this.loginModel().idPais, idToken: result.idToken }),
        );
      } finally {
        await this._entraIdService.clearSession(result.account);
      }
      await this._router.navigate(['home']);
    } catch (error) {
      this.showMicrosoftError(error);
    } finally {
      this.isLoggingInWithMicrosoft.set(false);
    }
  }

  private showMicrosoftError(error: unknown): void {
    if (error instanceof AuthError) {
      console.error(`Entra ID: ${error.errorCode}`, error);
      this.setFormError(MICROSOFT_ERROR_MESSAGES[error.errorCode] ?? MICROSOFT_GENERIC_ERROR_MESSAGE);
    } else if (isAppError(error)) {
      if (isCallerHandledError(error)) this.formError.set(error);
    } else {
      console.error(error);
      this.setFormError(MICROSOFT_GENERIC_ERROR_MESSAGE);
    }
  }

  private setFormError(message: string): void {
    this.formError.set({ statusCode: 0, title: 'Microsoft', messages: [message] });
  }
}
